import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Thermometer,
  Droplets,
  Zap,
  Battery,
  Activity,
  RefreshCw,
  Download,
  Calendar,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  BarChart3,
  LineChart as LineChartIcon,
  Settings,
  Play,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLanguage } from "./i18n/LanguageContext";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sensor type configurations
const SENSOR_TYPES = {
  temperature: {
    name: "Temperatura",
    icon: Thermometer,
    unit: "°C",
    color: "#ef4444",
    gradient: ["#fca5a5", "#ef4444"],
    thresholds: { low: 18, high: 25 }
  },
  humidity: {
    name: "Umidità",
    icon: Droplets,
    unit: "%",
    color: "#06b6d4",
    gradient: ["#a5f3fc", "#06b6d4"],
    thresholds: { low: 30, high: 60 }
  },
  power: {
    name: "Potenza",
    icon: Zap,
    unit: "W",
    color: "#eab308",
    gradient: ["#fef08a", "#eab308"],
    thresholds: { low: 0, high: 1000 }
  },
  voltage: {
    name: "Tensione",
    icon: Zap,
    unit: "V",
    color: "#3b82f6",
    gradient: ["#93c5fd", "#3b82f6"],
    thresholds: { low: 200, high: 250 }
  },
  current: {
    name: "Corrente",
    icon: Activity,
    unit: "A",
    color: "#10b981",
    gradient: ["#6ee7b7", "#10b981"],
    thresholds: { low: 0, high: 10 }
  },
  energy: {
    name: "Energia",
    icon: Activity,
    unit: "kWh",
    color: "#22c55e",
    gradient: ["#bbf7d0", "#22c55e"],
    thresholds: { low: 0, high: 100 }
  },
  battery: {
    name: "Batteria",
    icon: Battery,
    unit: "%",
    color: "#8b5cf6",
    gradient: ["#ddd6fe", "#8b5cf6"],
    thresholds: { low: 20, high: 80 }
  }
};

// Time period options
const TIME_PERIODS = [
  { value: "1", label: "Ultima ora" },
  { value: "6", label: "Ultime 6 ore" },
  { value: "12", label: "Ultime 12 ore" },
  { value: "24", label: "Ultime 24 ore" },
  { value: "48", label: "Ultimi 2 giorni" },
  { value: "168", label: "Ultima settimana" },
  { value: "720", label: "Ultimo mese" },
  { value: "1440", label: "Ultimi 2 mesi" },
  { value: "2160", label: "Ultimi 3 mesi" },
  { value: "4320", label: "Ultimi 6 mesi" },
  { value: "8760", label: "Ultimo anno" }
];

// Interval options
const INTERVAL_OPTIONS = [
  { value: "minute", label: "Minuti" },
  { value: "hour", label: "Ore" },
  { value: "day", label: "Giorni" }
];

// Device name mapping - fallback for technical names
const DEVICE_NAME_MAP = {
  "SNZB-02D": "Sensore Temperatura/Umidità",
  "c2c-humidity": "Sensore Umidità",
  "c2c-switch": "Interruttore Smart",
  "switchTemperatureSensor": "Sensore Temperatura Aria"
};

// Helper to get friendly device name
const getFriendlyDeviceName = (deviceName) => {
  if (!deviceName) return "Sensore";
  
  // Check if it's a technical name that needs mapping
  if (DEVICE_NAME_MAP[deviceName]) {
    return DEVICE_NAME_MAP[deviceName];
  }
  
  // If it starts with technical prefixes, try to clean it up
  if (deviceName.startsWith("c2c-") || deviceName.startsWith("switch")) {
    return deviceName
      .replace("c2c-", "")
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }
  
  // Return as-is if it looks like a real name
  return deviceName;
};

// Sensor Card Component
const SensorCard = ({ sensor, onClick, isSelected }) => {
  const config = SENSOR_TYPES[sensor.sensor_type] || SENSOR_TYPES.temperature;
  const Icon = config.icon;
  
  // Determine trend
  const getTrend = () => {
    if (sensor.avg > sensor.current_value) return { icon: TrendingDown, color: "text-blue-500", label: "In calo" };
    if (sensor.avg < sensor.current_value) return { icon: TrendingUp, color: "text-red-500", label: "In aumento" };
    return { icon: Minus, color: "text-gray-500", label: "Stabile" };
  };
  
  const trend = getTrend();
  const TrendIcon = trend.icon;
  
  // Value status
  const getValueStatus = () => {
    const value = sensor.current_value;
    if (value < config.thresholds.low) return "text-blue-500";
    if (value > config.thresholds.high) return "text-orange-500";
    return "text-green-500";
  };

  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-lg ${
        isSelected ? 'ring-2 ring-cyan-500 bg-cyan-500/10' : 'hover:bg-slate-50'
      }`}
      onClick={() => onClick(sensor)}
      data-testid={`sensor-card-${sensor.device_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="p-2 rounded-lg"
              style={{ backgroundColor: `${config.color}20` }}
            >
              <Icon className="h-5 w-5" style={{ color: config.color }} />
            </div>
            <div>
              <p className="font-medium text-sm truncate max-w-[150px]" title={sensor.device_name}>
                {getFriendlyDeviceName(sensor.device_name)}
              </p>
              <p className="text-xs text-gray-500">{config.name}</p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-2xl font-bold ${getValueStatus()}`}>
              {sensor.current_value !== null ? sensor.current_value : "--"}
              <span className="text-sm font-normal text-gray-500">{config.unit}</span>
            </p>
            <div className="flex items-center gap-1 justify-end text-xs">
              <TrendIcon className={`h-3 w-3 ${trend.color}`} />
              <span className={trend.color}>{trend.label}</span>
            </div>
          </div>
        </div>
        
        {/* Mini stats */}
        <div className="mt-3 pt-3 border-t grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-xs text-gray-500">Min</p>
            <p className="text-sm font-medium text-blue-600">{sensor.min}{config.unit}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Media</p>
            <p className="text-sm font-medium">{sensor.avg}{config.unit}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Max</p>
            <p className="text-sm font-medium text-red-600">{sensor.max}{config.unit}</p>
          </div>
        </div>
        
        <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
          <span>{sensor.readings_count} letture</span>
          <span>
            <Clock className="h-3 w-3 inline mr-1" />
            {new Date(sensor.last_update).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// Chart Component
const SensorChart = ({ data, sensorType, title }) => {
  const config = SENSOR_TYPES[sensorType] || SENSOR_TYPES.temperature;
  
  if (!data || data.labels?.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center text-gray-500">
        <div className="text-center">
          <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>Nessun dato disponibile</p>
          <p className="text-sm">Avvia la raccolta dati per visualizzare i grafici</p>
        </div>
      </div>
    );
  }

  // Format data for recharts
  const chartData = data.labels.map((label, index) => {
    const hasTime = label.includes('T');
    return {
      time: hasTime ? label.split('T')[1] : label.split('-').slice(1).join('/'), // "14:00" or "03/24"
      fullTime: label,
      avg: data.datasets.avg[index],
      min: data.datasets.min[index],
      max: data.datasets.max[index]
    };
  });

  return (
    <div className="h-[300px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`gradient-${sensorType}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={config.color} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={config.color} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey="time" 
            tick={{ fontSize: 11 }}
            tickFormatter={(value) => value.substring(0, 5)}
          />
          <YAxis 
            tick={{ fontSize: 11 }}
            domain={['auto', 'auto']}
            tickFormatter={(value) => `${value}${config.unit}`}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
            labelStyle={{ color: '#9ca3af' }}
            formatter={(value, name) => [`${value}${config.unit}`, name]}
            labelFormatter={(label, payload) => payload[0]?.payload?.fullTime || label}
          />
          <Legend />
          <Area
            type="monotone"
            dataKey="avg"
            stroke={config.color}
            fillOpacity={1}
            fill={`url(#gradient-${sensorType})`}
            name="Media"
            strokeWidth={2}
          />
          <Line
            type="monotone"
            dataKey="min"
            stroke="#3b82f6"
            strokeDasharray="5 5"
            dot={false}
            name="Min"
          />
          <Line
            type="monotone"
            dataKey="max"
            stroke="#ef4444"
            strokeDasharray="5 5"
            dot={false}
            name="Max"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default function SensorReport({ authToken }) {
  const { t } = useLanguage();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);
  const [collectionStatus, setCollectionStatus] = useState(null);
  const [energySummary, setEnergySummary] = useState(null);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [timePeriod, setTimePeriod] = useState("24");
  const [chartInterval, setChartInterval] = useState("hour");
  const [activeTab, setActiveTab] = useState("overview");

  // Load collection status
  const loadCollectionStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sensors/collection-status`);
      setCollectionStatus(response.data);
    } catch (error) {
      console.error("Error loading collection status:", error);
    }
  }, []);

  // Load energy summary
  const loadEnergySummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sensors/energy-summary?hours=${timePeriod}`);
      setEnergySummary(response.data);
    } catch (error) {
      console.error("Error loading energy summary:", error);
    }
  }, [timePeriod]);

  // Load report
  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sensors/report?hours=${timePeriod}&token=${authToken}`);
      setReport(response.data);
      
      // Auto-select first sensor if none selected
      if (!selectedSensor && response.data.sensors?.length > 0) {
        const firstSensor = response.data.sensors[0];
        setSelectedSensor(firstSensor);
        loadChartData(firstSensor.device_id, firstSensor.sensor_type);
      }
    } catch (error) {
      console.error("Error loading report:", error);
      // If no data, show empty state
      setReport({ sensors: [], generated_at: new Date().toISOString() });
    } finally {
      setLoading(false);
    }
  }, [timePeriod, authToken]);

  // Load chart data for selected sensor
  const loadChartData = async (deviceId, sensorType) => {
    setChartLoading(true);
    try {
      const response = await axios.get(
        `${API}/sensors/chart-data/${deviceId}?sensor_type=${sensorType}&hours=${timePeriod}&interval=${chartInterval}&token=${authToken}`
      );
      setChartData(response.data);
    } catch (error) {
      console.error("Error loading chart data:", error);
      setChartData(null);
    } finally {
      setChartLoading(false);
    }
  };

  // Collect sensor data now
  const collectData = async () => {
    setCollecting(true);
    try {
      const response = await axios.post(`${API}/sensors/force-collect?token=${authToken}`);
      toast.success(`Raccolte ${response.data.readings_saved} letture da ${response.data.sensors_count} sensori!`);
      await loadReport();
      await loadCollectionStatus();
    } catch (error) {
      console.error("Error collecting data:", error);
      toast.error("Errore nella raccolta dati");
    } finally {
      setCollecting(false);
    }
  };

  // Handle sensor selection
  const handleSensorSelect = (sensor) => {
    setSelectedSensor(sensor);
    loadChartData(sensor.device_id, sensor.sensor_type);
  };

  // Initial load
  useEffect(() => {
    loadReport();
    loadCollectionStatus();
    loadEnergySummary();
    
    // Refresh collection status every 30 seconds
    const statusInterval = window.setInterval(loadCollectionStatus, 30000);
    return () => clearInterval(statusInterval);
  }, [loadReport, loadCollectionStatus, loadEnergySummary]);

  // Reload chart when interval changes
  useEffect(() => {
    if (selectedSensor) {
      loadChartData(selectedSensor.device_id, selectedSensor.sensor_type);
    }
  }, [chartInterval]);

  // Group sensors by type
  const groupedSensors = report?.sensors?.reduce((acc, sensor) => {
    const type = sensor.sensor_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(sensor);
    return acc;
  }, {}) || {};

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2">Caricamento report sensori...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sensor-report">
      {/* Collection Status Banner */}
      {collectionStatus && (
        <Card className="bg-gradient-to-r from-emerald-50 to-cyan-50 border-emerald-200">
          <CardContent className="py-3 px-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${collectionStatus.background_collector_active ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
                <div>
                  <p className="text-sm font-medium text-emerald-800">
                    {collectionStatus.background_collector_active ? '🔄 Raccolta Automatica Attiva' : '⏸️ Raccolta Automatica Disattiva'}
                  </p>
                  <p className="text-xs text-emerald-600">
                    Intervallo: ogni {collectionStatus.collection_interval_minutes} minuti • 
                    Sorgente: {collectionStatus.source?.toUpperCase()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="text-center">
                  <p className="text-emerald-800 font-bold">{collectionStatus.readings_last_hour}</p>
                  <p className="text-emerald-600">Ultima ora</p>
                </div>
                <div className="text-center">
                  <p className="text-emerald-800 font-bold">{collectionStatus.readings_today}</p>
                  <p className="text-emerald-600">Oggi</p>
                </div>
                {collectionStatus.last_reading_timestamp && (
                  <div className="text-center">
                    <p className="text-emerald-800 font-bold">
                      {new Date(collectionStatus.last_reading_timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="text-emerald-600">Ultima lettura</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-blue-600" />
            {t.sensors.title}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Storico e analisi dei valori dei sensori
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Time period selector */}
          <Select value={timePeriod} onValueChange={setTimePeriod}>
            <SelectTrigger className="w-[160px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_PERIODS.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Interval selector */}
          <Select value={chartInterval} onValueChange={setChartInterval}>
            <SelectTrigger className="w-[120px]">
              <Clock className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INTERVAL_OPTIONS.map(i => (
                <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Refresh button */}
          <Button variant="outline" onClick={loadReport} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          
          {/* Collect data button */}
          <Button onClick={collectData} disabled={collecting}>
            {collecting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Raccogli Ora
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {(!report?.sensors || report.sensors.length === 0) && (
        <Card className="p-12 text-center">
          <Activity className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">{t.sensors.noData}</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Non ci sono ancora letture dei sensori nel database. 
            Clicca il pulsante Raccogli Ora per iniziare a raccogliere dati dai tuoi sensori eWeLink.
          </p>
          <Button onClick={collectData} disabled={collecting} size="lg">
            {collecting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Avvia Raccolta Dati
          </Button>
          <p className="text-xs text-gray-400 mt-4">
            Suggerimento: Per dati continui, configura una raccolta automatica ogni 5 minuti
          </p>
        </Card>
      )}

      {/* Main content */}
      {report?.sensors?.length > 0 && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Panoramica
            </TabsTrigger>
            <TabsTrigger value="temperature" className="flex items-center gap-2">
              <Thermometer className="h-4 w-4" />
              {t.sensors.temperature}
            </TabsTrigger>
            <TabsTrigger value="humidity" className="flex items-center gap-2">
              <Droplets className="h-4 w-4" />
              {t.sensors.humidity}
            </TabsTrigger>
            <TabsTrigger value="energy" className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              {t.sensors.energy}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Sensor Cards */}
              <div className="lg:col-span-1">
                <h3 className="font-medium mb-3 text-gray-700">Sensori Attivi</h3>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {report.sensors.map((sensor, idx) => (
                      <SensorCard
                        key={`${sensor.device_id}-${sensor.sensor_type}-${idx}`}
                        sensor={sensor}
                        onClick={handleSensorSelect}
                        isSelected={selectedSensor?.device_id === sensor.device_id && selectedSensor?.sensor_type === sensor.sensor_type}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Chart */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <LineChartIcon className="h-5 w-5 text-blue-600" />
                          {selectedSensor ? (
                            <>
                              {selectedSensor.device_name} - {SENSOR_TYPES[selectedSensor.sensor_type]?.name || selectedSensor.sensor_type}
                            </>
                          ) : (
                            "Seleziona un sensore"
                          )}
                        </CardTitle>
                        <CardDescription>
                          {TIME_PERIODS.find(p => p.value === timePeriod)?.label} - Intervallo: {INTERVAL_OPTIONS.find(i => i.value === chartInterval)?.label}
                        </CardDescription>
                      </div>
                      {chartLoading && <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <SensorChart 
                      data={chartData}
                      sensorType={selectedSensor?.sensor_type || 'temperature'}
                      title={selectedSensor?.device_name}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Temperature Tab */}
          <TabsContent value="temperature" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupedSensors.temperature?.map((sensor, idx) => (
                <SensorCard
                  key={`temp-${sensor.device_id}-${idx}`}
                  sensor={sensor}
                  onClick={handleSensorSelect}
                  isSelected={selectedSensor?.device_id === sensor.device_id}
                />
              ))}
            </div>
            {selectedSensor?.sensor_type === 'temperature' && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle>Andamento Temperatura - {selectedSensor.device_name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <SensorChart data={chartData} sensorType="temperature" />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Humidity Tab */}
          <TabsContent value="humidity" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupedSensors.humidity?.map((sensor, idx) => (
                <SensorCard
                  key={`hum-${sensor.device_id}-${idx}`}
                  sensor={sensor}
                  onClick={handleSensorSelect}
                  isSelected={selectedSensor?.device_id === sensor.device_id}
                />
              ))}
            </div>
            {selectedSensor?.sensor_type === 'humidity' && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle>Andamento Umidità - {selectedSensor.device_name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <SensorChart data={chartData} sensorType="humidity" />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Energy Tab */}
          <TabsContent value="energy" className="space-y-4">
            {/* Energy Summary Cards */}
            {energySummary && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Current Power */}
                <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-amber-600 font-medium">Potenza Attuale</p>
                        <p className="text-3xl font-bold text-amber-700">
                          {energySummary.total_power_w?.toFixed(0) || 0}
                          <span className="text-lg ml-1">W</span>
                        </p>
                      </div>
                      <div className="p-3 bg-amber-100 rounded-full">
                        <Zap className="h-6 w-6 text-amber-600" />
                      </div>
                    </div>
                    <p className="text-xs text-amber-500 mt-2">
                      Media: {energySummary.avg_power_w?.toFixed(0) || 0}W
                    </p>
                  </CardContent>
                </Card>

                {/* Daily Consumption */}
                <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-blue-600 font-medium">Consumo Giornaliero</p>
                        <p className="text-3xl font-bold text-blue-700">
                          {energySummary.consumption?.daily_kwh?.toFixed(2) || 0}
                          <span className="text-lg ml-1">kWh</span>
                        </p>
                      </div>
                      <div className="p-3 bg-blue-100 rounded-full">
                        <Activity className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <p className="text-xs text-blue-500 mt-2">
                      Costo: €{energySummary.consumption?.daily_cost_eur?.toFixed(2) || 0}
                    </p>
                  </CardContent>
                </Card>

                {/* Monthly Consumption */}
                <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-purple-600 font-medium">Stima Mensile</p>
                        <p className="text-3xl font-bold text-purple-700">
                          {energySummary.consumption?.monthly_kwh?.toFixed(1) || 0}
                          <span className="text-lg ml-1">kWh</span>
                        </p>
                      </div>
                      <div className="p-3 bg-purple-100 rounded-full">
                        <TrendingUp className="h-6 w-6 text-purple-600" />
                      </div>
                    </div>
                    <p className="text-xs text-purple-500 mt-2">
                      Costo: €{energySummary.consumption?.monthly_cost_eur?.toFixed(2) || 0}
                    </p>
                  </CardContent>
                </Card>

                {/* Cost per kWh */}
                <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-green-600 font-medium">Costo Energia</p>
                        <p className="text-3xl font-bold text-green-700">
                          €{energySummary.consumption?.cost_per_kwh?.toFixed(2) || 0.25}
                          <span className="text-lg ml-1">/kWh</span>
                        </p>
                      </div>
                      <div className="p-3 bg-green-100 rounded-full">
                        <Settings className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                    <p className="text-xs text-green-500 mt-2">
                      {energySummary.device_count || 0} dispositivi monitorati
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Power Sensors List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-amber-500" />
                  Sensori di Potenza
                </CardTitle>
                <CardDescription>
                  Dispositivi che misurano il consumo energetico in tempo reale
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedSensors.power?.length > 0 ? (
                    groupedSensors.power.map((sensor, idx) => (
                      <SensorCard
                        key={`power-${sensor.device_id}-${idx}`}
                        sensor={sensor}
                        onClick={handleSensorSelect}
                        isSelected={selectedSensor?.device_id === sensor.device_id && selectedSensor?.sensor_type === 'power'}
                      />
                    ))
                  ) : (
                    <div className="col-span-full text-center py-4 text-gray-500 text-sm">
                      Nessun sensore di potenza rilevato
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Voltage Sensors List */}
            {groupedSensors.voltage?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-blue-500" />
                    Sensori di Tensione
                  </CardTitle>
                  <CardDescription>
                    Misurazione tensione di rete (V)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {groupedSensors.voltage.map((sensor, idx) => (
                      <SensorCard
                        key={`voltage-${sensor.device_id}-${idx}`}
                        sensor={sensor}
                        onClick={handleSensorSelect}
                        isSelected={selectedSensor?.device_id === sensor.device_id && selectedSensor?.sensor_type === 'voltage'}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Current Sensors List */}
            {groupedSensors.current?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-green-500" />
                    Sensori di Corrente
                  </CardTitle>
                  <CardDescription>
                    Misurazione corrente assorbita (A)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {groupedSensors.current.map((sensor, idx) => (
                      <SensorCard
                        key={`current-${sensor.device_id}-${idx}`}
                        sensor={sensor}
                        onClick={handleSensorSelect}
                        isSelected={selectedSensor?.device_id === sensor.device_id && selectedSensor?.sensor_type === 'current'}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Chart for selected power sensor */}
            {selectedSensor?.sensor_type === "power" && chartData && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LineChartIcon className="h-5 w-5 text-amber-500" />
                    Storico Consumi - {selectedSensor.device_name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SensorChart data={chartData} sensorType="power" />
                </CardContent>
              </Card>
            )}
            
            {/* Chart for voltage sensor */}
            {selectedSensor?.sensor_type === "voltage" && chartData && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LineChartIcon className="h-5 w-5 text-blue-500" />
                    Storico Tensione - {selectedSensor.device_name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SensorChart data={chartData} sensorType="voltage" />
                </CardContent>
              </Card>
            )}
            
            {/* Chart for current sensor */}
            {selectedSensor?.sensor_type === "current" && chartData && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LineChartIcon className="h-5 w-5 text-green-500" />
                    Storico Corrente - {selectedSensor.device_name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SensorChart data={chartData} sensorType="current" />
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Info footer */}
      {report && (
        <div className="text-xs text-gray-400 flex items-center justify-between">
          <span>
            Report generato: {new Date(report.generated_at).toLocaleString('it-IT')}
          </span>
          <span>
            {report.sensors?.length || 0} sensori monitorati
          </span>
        </div>
      )}
    </div>
  );
}
